import { VIEW_TYPES } from "@/other/constants/OtherKeys";
import type { PageGuideDefinition } from "../types";

/** Target ids for Context page — use with `guideTargetProps()` or `data-guide`. */
export const CONTEXT_GUIDE_TARGETS = {
  workflowContext: "context-page-workflow",
  manifestLink: "context-page-manifest",
  newContext: "context-page-new",
  contextList: "context-page-list",
  contextDetail: "context-page-detail",
  emptyState: "context-page-empty",
  addContext: "context-page-add-context",
  contextFooter: "context-page-footer",
} as const;

export const contextPageGuide: PageGuideDefinition = {
  id: "context-page-v1",
  viewType: VIEW_TYPES.context,
  steps: [
    {
      target: CONTEXT_GUIDE_TARGETS.workflowContext,
      placement: { side: "bottom", align: "start" },
      title: "Your marketing stack",
      body: "Context is step one — business details and positioning that power your manifest, tags, and hubs.",
    },
    {
      target: CONTEXT_GUIDE_TARGETS.contextList,
      placement: { side: "right", align: "start" },
      title: "All your contexts",
      body: "Switch between contexts here. Each one captures a snapshot of your business you can refine or extend.",
    },
    {
      target: CONTEXT_GUIDE_TARGETS.contextDetail,
      placement: { side: "left", align: "start" },
      title: "Build and review",
      body: "Generate groups and statements for the selected context. Complete the wizard, then make a context active.",
    },
    {
      target: CONTEXT_GUIDE_TARGETS.newContext,
      placement: { side: "bottom", align: "end" },
      title: "Create another",
      body: "Start a fresh context anytime — useful for testing positioning or extending an active one.",
    },
    {
      target: CONTEXT_GUIDE_TARGETS.manifestLink,
      placement: { side: "bottom", align: "start" },
      title: "Next: Manifest",
      body: "Once a context is active, open Manifest to see the read-only blueprint Swivu compiles from it.",
    },
    {
      target: CONTEXT_GUIDE_TARGETS.addContext,
      placement: { side: "top", align: "end" },
      title: "Extend this context",
      body: "Add Context builds on the current one — duplicate and extend when you want to evolve positioning without starting over.",
    },
    {
      target: CONTEXT_GUIDE_TARGETS.contextFooter,
      placement: { side: "top", align: "center" },
      title: "Activate or view manifest",
      body: "Make a completed context active to refresh the manifest, or view the manifest when this context is already active.",
    },
  ],
};

export const contextPageSetupGuide: PageGuideDefinition = {
  id: "context-page-v1-setup",
  viewType: VIEW_TYPES.context,
  steps: [
    {
      target: CONTEXT_GUIDE_TARGETS.workflowContext,
      placement: { side: "bottom", align: "start" },
      title: "Start here",
      body: "Context is where you teach Swivu about your business before anything downstream is generated.",
    },
    {
      target: CONTEXT_GUIDE_TARGETS.newContext,
      placement: { side: "bottom", align: "end" },
      title: "Create your first",
      body: "Click New Context to launch the setup wizard — you'll answer a few questions about your organization.",
    },
    {
      target: CONTEXT_GUIDE_TARGETS.contextList,
      placement: { side: "right", align: "start" },
      title: "Context list",
      body: "Every context you create appears here. Select one to continue where you left off.",
    },
    {
      target: CONTEXT_GUIDE_TARGETS.emptyState,
      placement: { side: "left", align: "center" },
      title: "Pick a context",
      body: "Choose a context from the list to view or finish setup. Your detail panel opens here.",
    },
  ],
};
